<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcFixedAssetRentalCard',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    fixedAsset: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_2248226175642056_style = computed(() => ({
    minWidth: 0,
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_2248226175642056_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_1889980785424776_emit_ix4coy88g_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_1889980785424776_close_j06op4n14_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_1889980785424776_showModal_e33pjybm0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'AddFixedAssetToRentalModal',
      props: {
        asset: props.fixedAsset,
      },
    };
  };

  const act_1889980785424776_emit_ix4coy88g = new actionTypes.EmitAction({
    actionArgs: act_1889980785424776_emit_ix4coy88g_getActionArgs,
  });
  const inst_evh_1889980785424776_close_j06op4n14 =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_emit_ix4coy88g,
      event: 'close',
      when: inst_evh_1889980785424776_close_j06op4n14_getWhen,
      displayName: 'emit',
    });
  const act_1889980785424776_showModal_e33pjybm0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_1889980785424776_showModal_e33pjybm0_getActionArgs,
      events: [inst_evh_1889980785424776_close_j06op4n14],
    });
  const inst_evh_1889980785424776_clickPreventStop_wf22iqn3o =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_showModal_e33pjybm0,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_1889980785424776_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1889980785424776_clickPreventStop_wf22iqn3o],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_1889980785424776_close_j06op4n14,
    inst_evh_1889980785424776_clickPreventStop_wf22iqn3o,
    inst_evh_1889980785424776_clickPreventStop,
  };

  const evh_1889980785424776_close_j06op4n14 = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_close_j06op4n14.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickPreventStop_wf22iqn3o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop_wf22iqn3o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center">
    <div :style="b_2248226175642056_style">
      <ItpBox class="mb-2">
        <ItpText
          strong
          :text="fixedAsset.rental.name"
          no-wrap
          v-if="fixedAsset.rental"
        >
        </ItpText>
        <ItpLink
          :to="{
            name: 'app.rental',
            params: { id: fixedAsset.rental.id },
          }"
          icon="fa-external-link-square-alt"
          class="ms-2"
          v-if="fixedAsset.rental"
        >
        </ItpLink>
      </ItpBox>
      <ItpBox class="mb-2">
        <ItpText
          strong
          :text="fixedAsset.rental.client_name"
          no-wrap
          v-if="fixedAsset.rental && fixedAsset.rental.client_id"
        >
        </ItpText>
        <ItpLink
          :to="{
            name: 'app.company',
            params: { id: fixedAsset.rental.client_id },
          }"
          icon="fa-external-link-square-alt"
          class="ms-2"
          v-if="fixedAsset.rental && fixedAsset.rental.client_id"
        >
        </ItpLink>
      </ItpBox>
      <ItpButton
        :text="pgettext('FixedAsset', 'Add to rental...')"
        variant="light"
        size="sm"
        v-if="!readonly"
        @click.prevent.stop="evh_1889980785424776_clickPreventStop($event, {})"
      >
      </ItpButton>
    </div>
  </ItpBox>
</template>
